<template>
    <div v-if="erro" class="alert alert-danger mt-3" role="alert">{{ erro }}</div>
    <div v-if="entrada.id" class="card card-body shadow-sm mt-3">
      <!-- Cabeçalho -->
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">Detalhes da Entrada nº {{ entrada.id }}</h4>
        <button class="btn btn-secondary" @click="voltar">Voltar</button>
      </div>
  
      <!-- Dados do Fornecedor -->
      <FormSection title="Dados do Fornecedor">
        <div class="info-group">
          <strong>Fornecedor:</strong> {{ entrada.fornecedor_nome }}
        </div>
        <div class="info-group">
          <strong>Pendente:</strong>
          <span :class="entrada.pendente ? 'text-danger' : 'text-success'">
            {{ entrada.pendente ? ' Sim' : ' Não' }}
          </span>
        </div>
      </FormSection>
  
      <!-- Itens de Entrada -->
      <FormSection title="Itens de Entrada">
        <div class="table-responsive">
          <table class="table table-bordered mb-0">
            <thead class="table-light">
              <tr>
                <th>Produto</th>
                <th>Qtd.</th>
                <th>Preço Unit.</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in itens" :key="i">
                <td>{{ item.produto_nome }}</td>
                <td>{{ item.quantidade }}</td>
                <td>{{ globalUtils.formatarValor(item.preco_unit) }}</td>
                <td>{{ globalUtils.formatarValor(item.subtotal) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3" class="text-end">Total:</th>
                <th>{{ globalUtils.formatarValor(totalEntrada) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </FormSection>
  
      <!-- Pagamentos -->
      <FormSection v-if="!entrada.pendente" title="Detalhes do Pagamento">
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead class="table-light">
              <tr>
                <th>Parcela</th>
                <th>Forma de Pagamento</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Pagamento</th>
                <th>Conta Financeira</th>
                <th v-if="showChequeCols">Banco</th>
                <th v-if="showChequeCols">Agência</th>
                <th v-if="showChequeCols">Cheque nº</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in pagamentos" :key="i">
                <td>{{ i + 1 }}</td>
                <td>{{ p.forma }}</td>
                <td>{{ globalUtils.formatarData(p.data_vencimento) }}</td>
                <td>{{ globalUtils.formatarValor(p.valor) }}</td>
                <td>{{ p.data_pagamento ? globalUtils.formatarData(p.data_pagamento) : '–' }}</td>
                <td>{{ p.conta_finance }}</td>
                <td v-if="showChequeCols">{{ p.banco_numero || '–' }}</td>
                <td v-if="showChequeCols">{{ p.agencia || '–' }}</td>
                <td v-if="showChequeCols">{{ p.cheque_numero || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </FormSection>
  
      <div class="mt-3">
        <em>Entrada registrada por: {{ entrada.por || 'Desconhecido' }}</em>
      </div>
    </div>
  
    <div v-else-if="!erro">
      <LoadingSpinner />
    </div>
  </template>
  
  <script setup>
  import { reactive, ref, computed, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import axios from '@/services/axios';
  import FormSection from '@/components/Layout/FormSection.vue';
  import globalUtils from '@/utils/globalUtils';
  import LoadingSpinner from '@/utils/LoadingSpinner.vue';
  
  const router = useRouter();
  const route = useRoute();
  const erro = ref('');
  
  // Estado principal
  const entrada    = reactive({
    id: null,
    fornecedor_nome: '',
    pendente: false,
    parcelas: 1,
    por: ''
  });
  const itens       = ref([]);
  const pagamentos  = ref([]);
  
  // Exibe colunas de cheque se houver algum pagamento em cheque
  const showChequeCols = computed(() =>
    pagamentos.value.some(p => p.forma.toLowerCase() === 'cheque')
  );
  
  // Computed: total da entrada
  const totalEntrada = computed(() =>
    itens.value.reduce((sum, i) => sum + i.subtotal, 0)
  );
  
  // Carrega detalhes da entrada, itens e pagamentos
  async function carregarEntrada(id) {
    const { data } = await axios.get(`/loja/entradas/${id}`);
    Object.assign(entrada, {
      id: data.entrada.id,
      fornecedor_nome: data.entrada.fornecedor_nome,
      pendente: data.entrada.pendente,
      parcelas: data.entrada.parcelas,
      por: data.entrada.por
    });
  
    // Itens
    itens.value = data.itens.map(i => ({
      produto_nome: i.produto_nome,
      quantidade:   i.quantidade,
      preco_unit:   parseFloat(i.preco_unit),
      subtotal:     parseFloat(i.subtotal)
    }));
  
    // Pagamentos (se não pendente)
    if (!data.entrada.pendente) {
      pagamentos.value = data.pagamentos.map(p => {
        return {
          forma:            p.forma_pagamento || '–',
          data_vencimento:  p.data_vencimento,
          valor:            Number(p.valor),
          data_pagamento:   p.data_pagamento,
          banco_numero:     p.banco_numero,
          agencia:          p.agencia,
          cheque_numero:    p.cheque_numero,
          conta_finance:    p.conta_financeira_apelido || '–'
        };
      });
    }
  }
  
  onMounted(async () => {
    try {
      if (!route.params.id) throw new Error('ID ausente');
      await carregarEntrada(route.params.id);
    } catch (error) {
      const status = error.response?.status;
      erro.value = status === 403 ? 'Sem permissão para consultar os detalhes da entrada.'
        : status === 404 ? 'Entrada não encontrada.'
        : 'Não foi possível carregar todos os detalhes da entrada. Tente novamente.';
    }
  });

  function voltar() {
    router.push('/loja/entradas');
  }
  </script>
  
  <style scoped>
  .info-group {
    margin-bottom: 0.5rem;
  }
  .form-header {
    margin-bottom: 1rem;
  }
  </style>
  